import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Alert,
  Spinner,
  Button,
  Form,
  Row,
  Col,
  Container
} from 'reactstrap';
import instance from '../../api/Axios';
import InstitutionCampusDropdown from '../../components/InstitutionCampusDropdown';
import '../../styles/FormValidation.css';

const AddBeneficiary = () => {
  const { bursaryId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bursary, setBursary] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [emailChecking, setEmailChecking] = useState(false);

  const [formData, setFormData] = useState({
    national_id: '',
    title: '',
    surname: '',
    first_name_other: '',
    gender: '',
    race: '',
    email_address: '',
    mobile_number: '',
    institution: '',
    campus: '',
    field_of_study: '',
    student_number: '',
    faculty: '',
    year_of_study: '',
    id_document_type: 'pending',
    student_status: 'open',
    entry_date: '',
    exit_date: ''
  });

  useEffect(() => {
    fetchBursary();
  }, [bursaryId]);

  const fetchBursary = async () => {
    try {
      const response = await instance.get(`/api/bursaries/${bursaryId}/`);
      setBursary(response.data);
    } catch (error) {
      console.error('Error fetching bursary:', error);
      setError('Failed to load bursary information.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Check for duplicate student email
  const checkStudentEmailExists = async (email) => {
    try {
      const response = await instance.get(`accounts/check-email/?email=${encodeURIComponent(email)}&user_type=student`);
      return response.data.exists;
    } catch (error) {
      console.error('Error checking email:', error);
      return false; // If check fails, allow submission (server will handle it)
    }
  };

  // Handle email validation on blur
  const handleEmailBlur = async () => {
    if (formData.email_address && formData.email_address.trim()) {
      setEmailChecking(true);
      
      // Clear any existing email validation error
      if (validationErrors.email_address) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.email_address;
          return newErrors;
        });
      }

      const emailExists = await checkStudentEmailExists(formData.email_address);
      
      if (emailExists) {
        setValidationErrors(prev => ({
          ...prev,
          email_address: `A student with email "${formData.email_address}" already exists. This may cause issues with student bursary assignment.`
        }));
      }
      
      setEmailChecking(false);
    }
  };

  // Handle institution change from InstitutionCampusDropdown
  const handleInstitutionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      institution: value,
      campus: '' // Reset campus when institution changes
    }));
    
    // Clear validation errors
    if (validationErrors.institution) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.institution;
        return newErrors;
      });
    }
  };

  // Handle campus change from InstitutionCampusDropdown
  const handleCampusChange = (value) => {
    setFormData(prev => ({
      ...prev,
      campus: value
    }));
    
    // Clear validation error
    if (validationErrors.campus) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.campus;
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setValidationErrors({});

    try {
      const submitData = {
        ...formData,
        bursary: bursaryId
      };

      const response = await instance.post('/api/accounts/beneficiaries/', submitData);
      console.log('Beneficiary created:', response.data);
      
      setSuccess('Beneficiary added successfully!');
      setTimeout(() => {
        navigate(`/general-dashboard/bursaries/${bursaryId}`);
      }, 2000);
    } catch (error) {
      console.error('Error creating beneficiary:', error);
      if (error.response?.data) {
        // Handle field-specific validation errors
        if (typeof error.response.data === 'object') {
          setValidationErrors(error.response.data);
          setError('Please correct the errors below.');
        } else {
          const errorMessages = Object.values(error.response.data).flat();
          setError(errorMessages.join(', '));
        }
      } else {
        setError('Failed to add beneficiary. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!bursary) {
    return (
      <Container fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <Spinner color="primary" />
          <span className="ms-2">Loading bursary information...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">
            <i className="fas fa-user-plus me-2"></i>
            Add New Beneficiary
          </h4>
          <p className="text-muted mb-0">
            Adding beneficiary to: <strong>{bursary.name}</strong>
          </p>
        </div>
        <Button 
          color="secondary" 
          onClick={() => navigate(`/general-dashboard/bursaries/${bursaryId}`)}
          className="d-flex align-items-center"
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Bursary
        </Button>
      </div>

      {error && (
        <Alert color="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success">
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </Alert>
      )}

      <Card>
        <CardHeader className="bg-primary text-white">
          <h5 className="mb-0">
            <i className="fas fa-user me-2"></i>
            Beneficiary Information
          </h5>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSubmit} noValidate>
            {/* Personal Information */}
            <h6 className="text-primary mb-3">Personal Information</h6>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <select
                    className="form-select"
                    id="id_document_type"
                    name="id_document_type"
                    value={formData.id_document_type}
                    onChange={handleInputChange}
                  >
                    <option value="pending">Select ID Document Type</option>
                    <option value="passport">Passport</option>
                    <option value="national_id">National ID</option>
                    <option value="drivers_license">Drivers License</option>
                    <option value="refugee_travel_document">Refugee Travel Document</option>
                  </select>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.national_id ? 'is-invalid' : ''}`}
                    type="text"
                    id="national_id"
                    name="national_id"
                    value={formData.national_id}
                    onChange={handleInputChange}
                    placeholder="National ID"
                    maxLength="13"
                    required
                  />
                  <label htmlFor="national_id">National ID *</label>
                  {validationErrors.national_id && (
                    <div className="invalid-feedback d-block">
                      {Array.isArray(validationErrors.national_id) 
                        ? validationErrors.national_id.join(', ')
                        : validationErrors.national_id}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className="form-control"
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Title"
                  />
                  <label htmlFor="title">Title</label>
                </div>
              </Col>
              <Col md={6}>
                {/* Empty column for layout balance */}
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.surname ? 'is-invalid' : ''}`}
                    type="text"
                    id="surname"
                    name="surname"
                    value={formData.surname}
                    onChange={handleInputChange}
                    placeholder="Surname"
                    required
                  />
                  <label htmlFor="surname">Surname *</label>
                  {validationErrors.surname && (
                    <div className="invalid-feedback d-block">
                      {Array.isArray(validationErrors.surname) 
                        ? validationErrors.surname.join(', ')
                        : validationErrors.surname}
                    </div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.first_name_other ? 'is-invalid' : ''}`}
                    type="text"
                    id="first_name_other"
                    name="first_name_other"
                    value={formData.first_name_other}
                    onChange={handleInputChange}
                    placeholder="First Name / Other"
                    required
                  />
                  <label htmlFor="first_name_other">First Name / Other *</label>
                  {validationErrors.first_name_other && (
                    <div className="invalid-feedback d-block">
                      {Array.isArray(validationErrors.first_name_other) 
                        ? validationErrors.first_name_other.join(', ')
                        : validationErrors.first_name_other}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <select
                    className="form-select"
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className="form-control"
                    type="text"
                    id="race"
                    name="race"
                    value={formData.race}
                    onChange={handleInputChange}
                    placeholder="Race"
                  />
                  <label htmlFor="race">Race</label>
                </div>
              </Col>
            </Row>

            {/* Contact Information */}
            <h6 className="text-primary mb-3 mt-4">Contact Information</h6>
            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.email_address ? 'is-invalid' : ''}`}
                    type="email"
                    id="email_address"
                    name="email_address"
                    value={formData.email_address}
                    onChange={handleInputChange}
                    onBlur={handleEmailBlur}
                    placeholder="Email Address"
                    disabled={emailChecking}
                    required
                  />
                  <label htmlFor="email_address">Email Address *</label>
                  {emailChecking && (
                    <div className="text-info mt-1">
                      <small>Checking for existing students with this email...</small>
                    </div>
                  )}
                  {validationErrors.email_address && (
                    <div className="invalid-feedback d-block">
                      {Array.isArray(validationErrors.email_address) 
                        ? validationErrors.email_address.join(', ')
                        : validationErrors.email_address}
                    </div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.mobile_number ? 'is-invalid' : ''}`}
                    type="tel"
                    id="mobile_number"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleInputChange}
                    placeholder="Mobile Number"
                    required
                  />
                  <label htmlFor="mobile_number">Mobile Number *</label>
                  {validationErrors.mobile_number && (
                    <div className="invalid-feedback d-block">
                      {Array.isArray(validationErrors.mobile_number) 
                        ? validationErrors.mobile_number.join(', ')
                        : validationErrors.mobile_number}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Academic Information */}
            <h6 className="text-primary mb-3 mt-4">Academic Information</h6>
            
            <InstitutionCampusDropdown
              institutionValue={formData.institution}
              campusValue={formData.campus}
              onInstitutionChange={handleInstitutionChange}
              onCampusChange={handleCampusChange}
              institutionLabel="Institution"
              campusLabel="Campus"
              validationErrors={validationErrors}
            />

            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className="form-control"
                    type="text"
                    id="field_of_study"
                    name="field_of_study"
                    value={formData.field_of_study}
                    onChange={handleInputChange}
                    placeholder="Field of Study"
                    required
                  />
                  <label htmlFor="field_of_study">Field of Study *</label>
                  {validationErrors.field_of_study && (
                    <div className="invalid-feedback d-block">
                      {Array.isArray(validationErrors.field_of_study) 
                        ? validationErrors.field_of_study.join(', ')
                        : validationErrors.field_of_study}
                    </div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className="form-control"
                    type="text"
                    id="student_number"
                    name="student_number"
                    value={formData.student_number}
                    onChange={handleInputChange}
                    placeholder="Student Number"
                  />
                  <label htmlFor="student_number">Student Number</label>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className="form-control"
                    type="text"
                    id="faculty"
                    name="faculty"
                    value={formData.faculty}
                    onChange={handleInputChange}
                    placeholder="Faculty"
                  />
                  <label htmlFor="faculty">Faculty</label>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <select
                    className="form-select"
                    id="year_of_study"
                    name="year_of_study"
                    value={formData.year_of_study}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Year of Study</option>
                    {Array.from({ length: 27 }, (_, i) => 2000 + i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                {/* Hidden field for student status - always set to 'open' */}
                <input
                  type="hidden"
                  name="student_status"
                  value="open"
                />
              </Col>
            </Row>

            {/* Dates */}
            <h6 className="text-primary mb-3 mt-4">Registration Dates</h6>
            <Row>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className="form-control"
                    type="date"
                    id="entry_date"
                    name="entry_date"
                    value={formData.entry_date}
                    onChange={handleInputChange}
                    placeholder="Entry Date"
                  />
                  <label htmlFor="entry_date">Entry Date</label>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <input
                    className="form-control"
                    type="date"
                    id="exit_date"
                    name="exit_date"
                    value={formData.exit_date}
                    onChange={handleInputChange}
                    placeholder="Exit Date"
                  />
                  <label htmlFor="exit_date">Exit Date</label>
                </div>
              </Col>
            </Row>

            {/* Submit Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                color="secondary" 
                type="button"
                onClick={() => navigate(`/general-dashboard/bursaries/${bursaryId}`)}
              >
                Cancel
              </Button>
              <Button 
                color="primary" 
                type="submit" 
                disabled={loading}
                className="d-flex align-items-center"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Add Beneficiary
                  </>
                )}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </Container>
  );
};

export default AddBeneficiary;